import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { API_URL } from "../../config";

const Register = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    full_name: "",
  });

  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFileChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      avatar: e.target.files[0],
    }));
  };

  const handleGoogleLogin = async () => {
    try {
      const response = await axios.post(`${API_URL}/auth/start/google`);
      window.location.href = response.data.auth_url;
    } catch (err) {
      toast.error("Failed to initialize Google login. Please try again.");
    }
  };

  const handleOutlookLogin = async () => {
    try {
      const response = await axios.post(`${API_URL}/auth/start/outlook`);
      window.location.href = response.data.auth_url;
    } catch (err) {
      toast.error("Failed to initialize Outlook login. Please try again.");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setLoading(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append("email", formData.email);
      formDataToSend.append("password", formData.password);
      formDataToSend.append("full_name", formData.full_name);
      if (formData.avatar) {
        formDataToSend.append("avatar", formData.avatar);
      }

      const response = await axios.post(
        `${API_URL}/auth/register`,
        formDataToSend,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.data) {
        // Store the user's full name and other data
        const userData = {
          name: formData.full_name,
          email: formData.email,
          picture: "/default-avatar.svg",
        };
        localStorage.setItem("cached_user_data", JSON.stringify(userData));
        localStorage.setItem("user_name", formData.full_name);
        localStorage.setItem("user_email", formData.email);
        localStorage.setItem("user_picture", "/default-avatar.svg");

        // Set new user flag before redirecting
        localStorage.setItem("new_user_signup", "true");
        localStorage.setItem("has_seen_accounts_tour", "false");
        navigate("/email-verification");
      }
    } catch (err) {
      if (err.response && err.response.data) {
        // Check if there are password errors
        if (
          err.response.data.password &&
          Array.isArray(err.response.data.password)
        ) {
          // Join all password error messages with line breaks
          const passwordErrors = err.response.data.password.join("\n");
          toast.error(`Password issues:\n${passwordErrors}`);
        } else {
          // Handle other error formats or generic errors
          toast.error(
            "Registration failed: " +
              (err.response?.data?.message || "Please try again.")
          );
        }
      } else {
        toast.error("Registration failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#1F9BFF1A] flex">
      {/* Left side - Registration form */}
      <div className="w-full md:w-1/2 flex items-center justify-center">
        <div className="max-w-lg w-full space-y-4 bg-white p-4 md:p-8 rounded-2xl shadow-lg border border-gray-200 m-2 md:m-6">
          <div className="flex flex-col items-center justify-center p-4 md:p-8">
            <div className="self-start">
              <h2 className="text-3xl font-bold text-gray-800 mb-1">Sign up</h2>
              <p className="text-sm text-gray-600 mt-2">
                Already have an account?{" "}
                <span
                  className="text-blue-400 cursor-pointer"
                  onClick={() => navigate("/")}
                >
                  Sign in.
                </span>
              </p>
            </div>
            <ToastContainer
              position="top-right"
              autoClose={3000}
              hideProgressBar={false}
              newestOnTop={false}
              closeOnClick
              rtl={false}
              pauseOnFocusLoss
              draggable
              pauseOnHover
              theme="light"
            />
            <form onSubmit={handleSubmit} className="mt-6 space-y-4 w-full">
              <div>
                <label
                  htmlFor="full_name"
                  className="block text-sm font-medium text-gray-500"
                >
                  Full Name
                </label>
                <input
                  type="text"
                  id="full_name"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleChange}
                  required
                  disabled={loading}
                  className="block w-full py-0.6 border-b border-gray-300 focus:border-b-2 focus:border-blue-500 focus:outline-none disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-gray-500 mt-3"
                >
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  disabled={loading}
                  className="block w-full py-0.6 border-b border-gray-300 focus:border-b-2 focus:border-blue-500 focus:outline-none disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
                <div className="relative">
                  <label
                    htmlFor="password"
                    className="block text-sm font-medium text-gray-500 mt-3"
                  >
                    Password
                  </label>
                  <div className="flex items-center">
                    <input
                      type={formData.showPassword ? "text" : "password"}
                      id="password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      required
                      disabled={loading}
                      className="block w-full py-0.6 border-b border-gray-300 focus:border-b-2 focus:border-blue-500 focus:outline-none disabled:bg-gray-100 disabled:cursor-not-allowed"
                    />
                    <button
                      type="button"
                      onClick={() =>
                        setFormData((prev) => ({
                          ...prev,
                          showPassword: !prev.showPassword,
                        }))
                      }
                      disabled={loading}
                      className="absolute right-2 text-gray-500 focus:outline-none mb-2 disabled:opacity-50"
                    >
                      {formData.showPassword ? (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                          <path
                            fillRule="evenodd"
                            d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      ) : (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M3.707 2.293a1 1 0 00-1.414 1.414l14 14a1 1 0 001.414-1.414l-1.473-1.473A10.014 10.014 0 0019.542 10C18.268 5.943 14.478 3 10 3a9.958 9.958 0 00-4.512 1.074l-1.78-1.781zm4.261 4.26l1.514 1.515a2.003 2.003 0 012.45 2.45l1.514 1.514a4 4 0 00-5.478-5.478z"
                            clipRule="evenodd"
                          />
                          <path d="M12.454 16.697L9.75 13.992a4 4 0 01-3.742-3.741L2.335 6.578A9.98 9.98 0 00.458 10c1.274 4.057 5.065 7 9.542 7 .847 0 1.669-.105 2.454-.303z" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full py-2 px-6 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#1F9BFF] hover:bg-blue-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {loading ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Creating Account...
                  </>
                ) : (
                  "Sign up"
                )}
              </button>
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">Or</span>
                </div>
              </div>
              <div className="flex flex-col space-y-2">
                <button
                  type="button"
                  onClick={handleGoogleLogin}
                  className="w-full flex items-center justify-center gap-2 py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  <img
                    src="https://www.google.com/favicon.ico"
                    alt="Google"
                    className="w-5 h-5"
                  />
                  Continue with Google
                </button>

                <button
                  type="button"
                  onClick={handleOutlookLogin}
                  className="w-full flex items-center justify-center gap-2 py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  <img
                    src="https://www.microsoft.com/favicon.ico"
                    alt="Outlook"
                    className="w-5 h-5"
                  />
                  Continue with Outlook
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      {/* Right side - Blue section with text and logo */}
      <div className="hidden md:flex w-3/5 bg-[#1F9BFF1A] flex-col items-center justify-center p-12 text-white">
        <div className="space-y-6">
          <img src="/Efficia-01.svg" alt="Logo" className="h-15" />
          <h1 className="text-6xl font-bold text-blue-400 mb-4">
            One tool for you to manage Email
          </h1>
          <p className="text-lg text-gray-600">
            Manage your emails effortlessly with our smart email assistant -
            organize, prioritize, and respond with AI-powered efficiency.
          </p>
          <p className="text-lg text-gray-600">
            Create your account to start managing your tasks and stay organized.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;
